import React, { useState } from 'react';
import { Typography, Box, Button, IconButton, Divider } from '@mui/material';
import { Close as CloseIcon, LocalOffer as LocalOfferIcon } from '@mui/icons-material';
import { StyledTextField } from './common/StyledInputs';
import { useAuth } from '../contexts/AuthContext';
import * as subscriptionService from '../services/subscriptionService';
import config from '../config';
import { StyledDialog } from './common/StyledInputs';
import { colors } from '../styles';

const PricingModal = ({ open, onClose }) => {
  const [processing, setProcessing] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [showCoupon, setShowCoupon] = useState(false);
  const { user } = useAuth();

  const handleUpgrade = async () => {
    try {
      setProcessing(true);
      const returnUrl = window.location.pathname + window.location.search;
      const { sessionUrl } = await subscriptionService.createCheckoutSession({ returnUrl, couponCode: couponCode?.trim() });
      window.location.href = sessionUrl;
    } catch (err) {
      setProcessing(false);
    }
  };

  // D22 (2026-10-12): Budgit has ONE free trial — the 90-day Budgit trial.
  // Paid Stripe checkout carries no separate free trial.

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '24px',
          maxWidth: '400px !important',
          backgroundColor: colors.white,
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15)',
          overflow: 'visible'
        }
      }}
    >
      <Box sx={{ p: 4, textAlign: 'center', position: 'relative' }}>
        {/* Discount & Close Buttons */}
        <IconButton
          onClick={() => setShowCoupon((v) => !v)}
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            color: showCoupon ? colors.primary : 'text.disabled',
          }}
        >
          <LocalOfferIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: colors.secondary,
            opacity: 0.6,
            '&:hover': {
              opacity: 1,
              bgcolor: 'rgba(0,0,0,0.05)'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            color: colors.secondary,
            mb: 1,
            fontSize: '1.75rem'
          }}>
            Unlock Premium
          </Typography>
          <Typography variant="body1" sx={{ 
            color: colors.secondary, 
            opacity: 0.7,
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            Get full access to all features
          </Typography>
        </Box>

        {/* Pricing — Stripe/web Founding list price only. No strikethrough MSRP. */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              alignItems: 'baseline', 
              gap: 1,
              mb: 1
            }}>
              <Typography variant="h2" sx={{ 
                fontWeight: 800, 
                color: colors.primary,
                fontSize: '3rem',
                lineHeight: 1
              }}>
                ${config.PRICING_DISPLAY.PREMIUM_FOUNDER_MONTHLY}
              </Typography>
              <Typography variant="body1" sx={{ 
                color: colors.secondary,
                opacity: 0.7,
                fontSize: '1rem'
              }}>
                /month
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'inline-block',
              bgcolor: colors.background, 
              color: colors.secondary,
              px: 2,
              py: 0.5,
              borderRadius: '20px',
              fontSize: '0.8rem',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
              Founding Member
            </Box>
          </Box>

          <Divider sx={{ my: 3, opacity: 0.3 }} />

          {/* Discount code (toggle) */}
          {showCoupon && (
            <Box sx={{ textAlign: 'left', mb: 2 }}>
              <StyledTextField
                label="Discount code"
                fullWidth
                placeholder="Enter code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
              />
            </Box>
          )}

          {/* Features */}
          <Box sx={{ textAlign: 'left', mb: 3 }}>
            {[
              'Connect unlimited bank accounts',
              'Automatic transaction categorization with AI',
              'Smart budgeting insights',
              'Plaid integration for real-time data'
            ].map((feature, index) => (
              <Box key={index} sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 1.5,
                color: colors.secondary
              }}>
                <Box sx={{ 
                  width: 6, 
                  height: 6, 
                  borderRadius: '50%', 
                  bgcolor: colors.primary, 
                  mr: 2,
                  flexShrink: 0
                }} />
                <Typography variant="body2" sx={{ fontSize: '0.95rem' }}>
                  {feature}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={() => {
              if (!user) return onClose();
              return handleUpgrade();
            }}
            disabled={processing}
            sx={{
              borderRadius: '50px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              fontSize: '0.95rem',
              bgcolor: colors.primary,
              color: colors.white,
              boxShadow: 'none',
              '&:hover': {
                bgcolor: colors.primary,
                opacity: 0.9,
                boxShadow: 'none'
              },
              '&:disabled': {
                bgcolor: `${colors.primary}50`,
                color: colors.white,
                boxShadow: 'none'
              }
            }}
          >
            {!user ? 'Get Started' : 'Upgrade Now'}
          </Button>
          
          <Button
            fullWidth
            variant="text"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              color: colors.secondary,
              opacity: 0.6,
              '&:hover': {
                opacity: 1,
                bgcolor: 'transparent'
              }
            }}
          >
            Maybe later
          </Button>
        </Box>

        <Typography variant="caption" sx={{ 
          display: 'block',
          mt: 2,
          color: colors.secondary,
          opacity: 0.5,
          fontSize: '0.8rem'
        }}>
          Auto-renews monthly. Cancel anytime.
        </Typography>
      </Box>
    </StyledDialog>
  );
};

export default PricingModal;